import React, { useMemo, useState, useEffect } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { Card, Progress, Note, Avatar, AwardBadge, AwardTrophy, Btn, SectionHeader, PilulaAwardTime } from "./ui";
import { supabase, isSupabaseReady } from "./supabase";
import { Icon } from "./Icons";
import { AWARDS, TIMES } from "./data";
import {
  calcPontosCorretor, calcAwardVitalicio, calcProximoAward,
  calcElegibilidadePremios,
  calcPorTime, calcPorRegional, calcNacional, statusMetaColetiva,
  infoRankingSemanal, calcBonusRankingIndividual, rankingMensalNacional,
  calcAceleradoresCorretor, calcMetasIndividuais,
} from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// DASHBOARD DO CORRETOR
// Home unificada: pódio → perfil → prêmios → time → regional → nacional
// ═══════════════════════════════════════════════════════════════════════

export default function Dashboard({ currentUser, corretores, metas, aceleradores, premios, times, metasIndividuais, onNavigate }) {
  const meu = useMemo(
    () => corretores.find(c => c._uuid === currentUser._corretorUuid || c.id === currentUser.corretorId),
    [corretores, currentUser.corretorId, currentUser._corretorUuid]
  );

  if (!meu) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>
        Corretor não encontrado no sistema.
      </div>
    );
  }

  // Agregações que usaremos em vários lugares
  const pts = calcPontosCorretor(meu);
  const award = calcAwardVitalicio(meu);
  const prox = calcProximoAward(pts.total);
  const elegibilidade = calcElegibilidadePremios(meu);
  const time = (times || TIMES).find(t => t.id === meu.timeId);

  const timesAgg = useMemo(() => calcPorTime(corretores, times), [corretores, times]);

  // Ranking de times (semana — ordena por volume desc, só times válidos com ≥3 membros)
  const rankingTimes = useMemo(() =>
    timesAgg
      .filter(t => t.valido)
      .slice()
      .sort((a, b) => (b.valor || 0) - (a.valor || 0))
      .map((t, i) => ({ ...t, posicao: i + 1 })),
    [timesAgg]
  );
  const regionais = useMemo(() => calcPorRegional(corretores), [corretores]);
  const nacional = useMemo(() => calcNacional(corretores, "mes"), [corretores]);
  const meuTime = timesAgg.find(t => t.time.id === meu.timeId);
  const minhaReg = regionais.find(r => r.uf === meu.uf);

  // Rankings temporais
  const rankSem = useMemo(() => infoRankingSemanal(corretores, meu.id), [corretores, meu.id]);
  const bonusIndiv = useMemo(() => calcBonusRankingIndividual(meu, corretores), [meu, corretores]);
  const metasIndiv = useMemo(() => calcMetasIndividuais(meu, metasIndividuais), [meu, metasIndividuais]);
  const topMes = useMemo(() => rankingMensalNacional(corretores).slice(0, 3), [corretores]);
  const minhaPosMes = topMes.findIndex(c => c.id === meu.id);

  const accelColetivo = useMemo(() =>
    calcAceleradoresCorretor({ corretor: meu, times: timesAgg, regionais, nacional, metas, aceleradores }),
    [meu, timesAgg, regionais, nacional, metas, aceleradores]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* 1️⃣ PÓDIO DA SEMANA — impacto visual primeiro */}
      <PodioSemana
        top3={rankSem.top3}
        usuario={rankSem.usuario}
        meuId={meu.id}
        restante={rankSem.restante}
        times={times || TIMES}
        aceleradores={aceleradores}
        onVerCompleto={() => onNavigate?.("ranking")}
      />

      {/* 2️⃣ ATALHOS DE AÇÃO */}
      <AtalhosRapidos onNavigate={onNavigate} userModules={currentUser.modules} />

      {/* 3️⃣ PERFIL COMPACTO */}
      <PerfilCompacto
        corretor={meu} pts={pts} award={award} prox={prox}
        time={time} bonusTotal={accelColetivo.total}
      />

      {/* 4️⃣ SUA SEMANA — metas individuais + bônus de ranking */}
      <SuaSemana metasIndiv={metasIndiv} bonusDetalhes={bonusIndiv.detalhes} bonusPontos={bonusIndiv.pontosBonus} />

      {/* 5️⃣ PRÊMIOS ANUAIS */}
      <PremiosAnuais elegibilidade={elegibilidade} premios={premios} />

      {/* 5️⃣b BÔNUS DE TARGETS (individual) */}
      <BonusTargetsCard corretorUuid={currentUser._corretorUuid} />

      {/* 6️⃣ MEU TIME (semanal) */}
      {meuTime && meuTime.valido && (() => {
        const minhaPos = rankingTimes.find(t => t.time.id === meuTime.time.id)?.posicao;
        const totalTimesValidos = rankingTimes.length;
        return (
          <MetaCard
            titulo={`${meuTime.time.emoji || "🎯"} Meu Time · semana`}
            nome={meuTime.time.nome}
            valor={meuTime.valor} ativados={meuTime.ativados}
            metaVol={metas.time.metaVolume} metaAtiv={metas.time.metaAtivacoes}
            onVerMais={() => onNavigate?.("times")}
            subtitle={`${meuTime.membros.length} ${meuTime.membros.length === 1 ? "corretor" : "corretores"}${minhaPos ? ` · #${minhaPos} de ${totalTimesValidos} no ranking` : ""}`}
          />
        );
      })()}

      {meuTime && !meuTime.valido && (
        <Note tone="warn" icon={<Icon.alert size={14} />}>
          Seu time tem menos de 3 corretores — meta de time pausada enquanto não atingir o mínimo. Você continua participando das metas Regional e Nacional normalmente.
        </Note>
      )}

      {!meu.timeId && (
        <Note tone="info" icon={<Icon.user size={14} />}>
          <b>🌅 Você é um Corretor Pioneiro</b> — não está em nenhum time. Recrute mais corretores na sua praça ou peça ao admin para agrupar você em um time. Enquanto isso, você ganha todos os benefícios individuais normalmente.
        </Note>
      )}

      {/* 7️⃣ MINHA REGIONAL (mensal) */}
      {minhaReg && (
        <MetaCard
          titulo={`🗺️ Regional ${meu.uf} · mês`}
          nome={`${minhaReg.cidades.join(", ")}`}
          valor={minhaReg.valor} ativados={minhaReg.ativados}
          metaVol={metas.regional.metaVolume} metaAtiv={metas.regional.metaAtivacoes}
          onVerMais={() => onNavigate?.("regional")}
          subtitle={`${minhaReg.membros.length} corretores na regional`}
        />
      )}

      {/* 8️⃣ NACIONAL (mensal) */}
      <MetaCard
        titulo={`🌎 Nacional · mês`}
        nome="Toda a operação Bellanor"
        valor={nacional.valor} ativados={nacional.ativados}
        metaVol={metas.nacional.metaVolume} metaAtiv={metas.nacional.metaAtivacoes}
        onVerMais={() => onNavigate?.("nacional")}
        subtitle={`${nacional.estadosAtivos} ${nacional.estadosAtivos === 1 ? "estado ativo" : "estados ativos"}`}
      />

      {/* 9️⃣ TOP 3 DO MÊS (se houver) */}
      {topMes.length > 0 && (
        <TopMesCard top3={topMes} meuId={meu.id} minhaPos={minhaPosMes} times={times || TIMES} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════

// ─── PÓDIO DA SEMANA (hero emocional) ────────────────────────────────
function PodioSemana({ top3, usuario, meuId, restante, onVerCompleto, times, aceleradores }) {
  if (!top3 || top3.length === 0) {
    return (
      <Card style={{
        background: T.gradient.cardHero,
        border: `1px solid ${T.borderMid}`,
      }}>
        <div style={{ textAlign: "center", padding: "30px 20px" }}>
          <Icon.trophy size={40} />
          <div style={{ marginTop: 14, fontSize: 14, color: T.textSec }}>
            A semana mal começou — seja o primeiro a pontuar!
          </div>
        </div>
      </Card>
    );
  }

  const souEu1 = top3[0]?.id === meuId;

  return (
    <div style={{
      background: T.gradient.cardHero,
      border: `1px solid ${T.borderMid}`,
      borderRadius: T.r.xl, padding: "24px 20px",
      position: "relative", overflow: "hidden",
      boxShadow: T.shadow.lg,
    }}>
      {/* Glow dourado sutil acima */}
      <div style={{
        position: "absolute", top: -60, left: "50%", transform: "translateX(-50%)",
        width: 400, height: 200,
        background: `radial-gradient(ellipse, ${T.gold}18 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10, position: "relative" }}>
        <div>
          <div className="serif" style={{
            fontSize: 22, fontWeight: 600, color: T.gold,
            letterSpacing: -0.3, display: "flex", alignItems: "center", gap: 10,
          }}>
            🏆 Ranking da Semana
          </div>
          <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
            Prestígio individual · Prêmios em jogo
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>Termina em</div>
          <div className="serif" style={{ fontSize: 20, color: T.gold, fontWeight: 600, letterSpacing: -0.5, marginTop: 2 }}>
            {restante.formatado}
          </div>
        </div>
      </div>

      {/* Pódio compacto */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr 1fr", gap: 10, alignItems: "end", marginBottom: 16, position: "relative" }} className="podium-grid">
        {[top3[1], top3[0], top3[2]].map((c, i) => {
          if (!c) return <div key={i} />;
          const posReal = i === 0 ? 2 : i === 1 ? 1 : 3;
          const aw = calcAwardVitalicio(c);
          const cor = AWARD_COLORS[aw.cor];
          const souEu = c.id === meuId;
          const timeDoc = c.timeId && times ? times.find(t => t.id === c.timeId) : null;
          return (
            <PodioColuna
              key={c.id}
              corretor={c}
              posicao={posReal}
              color={cor}
              souEu={souEu}
              awardId={aw.id}
              timeEmoji={timeDoc?.emoji}
            />
          );
        })}
      </div>

      {/* Sua posição (se não está no top 3) */}
      {usuario && usuario.posicao > 3 && (
        <div style={{
          background: T.brandSoft,
          border: `1px solid ${T.brand}55`,
          borderRadius: T.r.md, padding: "10px 14px",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          marginTop: 8,
        }}>
          <div className="serif" style={{ fontSize: 18, color: T.gold, fontWeight: 700 }}>
            #{usuario.posicao}
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>Você</div>
            <div style={{ fontSize: 11, color: T.textSub }}>
              {usuario.pts} pontos na semana
              {usuario.proximoAcima && (
                <> · faltam <b style={{ color: T.gold }}>{usuario.proximoAcima.faltam}</b> pts para ultrapassar {usuario.proximoAcima.nome.split(" ")[0]}</>
              )}
            </div>
          </div>
        </div>
      )}

      {souEu1 && (
        <div style={{
          background: `linear-gradient(135deg, ${T.gold}22 0%, ${T.brand}18 100%)`,
          border: `1px solid ${T.gold}66`,
          borderRadius: T.r.md, padding: "10px 14px",
          display: "flex", alignItems: "center", gap: 12,
          marginTop: 8,
        }}>
          <span style={{ fontSize: 22 }}>🎯</span>
          <div style={{ fontSize: 12, color: T.gold, lineHeight: 1.5, fontWeight: 500 }}>
            <b>Você está em 1º!</b> Mantenha a liderança para ganhar as recompensas.
          </div>
        </div>
      )}

      {/* Regras dos prêmios (compacto) */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6,
        marginTop: 14, fontSize: 10, color: T.textSub,
      }} className="podium-rules">
        <div style={{ padding: "8px 10px", background: T.bg, borderRadius: T.r.sm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 3 }}>#1 Time</div>
          <div style={{ color: T.text, fontWeight: 600, lineHeight: 1.3 }}>+{aceleradores?.timeSupermeta ?? 0} pts<br/>acelerador</div>
        </div>
        <div style={{ padding: "8px 10px", background: T.bg, borderRadius: T.r.sm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 3 }}>#1 Regional</div>
          <div style={{ color: T.text, fontWeight: 600, lineHeight: 1.3 }}>+{aceleradores?.regionalSupermeta ?? 0} pts<br/>acelerador</div>
        </div>
        <div style={{ padding: "8px 10px", background: T.brandSoft, borderRadius: T.r.sm, border: `1px solid ${T.brand}66` }}>
          <div style={{ fontSize: 9, color: T.gold, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 3 }}>#1 Nacional</div>
          <div style={{ color: T.gold, fontWeight: 600, lineHeight: 1.3 }}>+{aceleradores?.nacionalSupermeta ?? 0} pts acelerador</div>
        </div>
      </div>

      {/* CTA ver completo */}
      <div style={{ textAlign: "center", marginTop: 14 }}>
        <Btn variant="ghost" onClick={onVerCompleto} icon={<Icon.arrow size={13} />}>
          Ver ranking completo
        </Btn>
      </div>
    </div>
  );
}

function PodioColuna({ corretor: c, posicao, color, souEu, awardId, timeEmoji }) {
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const isFirst = posicao === 1;
  const avatarSize = isFirst ? 66 : 52;
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  const primeiroNome = c.nome.split(" ")[0];

  return (
    <div className="podio-coluna" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      {isFirst && <div className="podio-coroa" style={{ fontSize: 20, lineHeight: 1 }}>👑</div>}

      {/* Avatar */}
      <div style={{ position: "relative" }}>
        <div className="podio-avatar-wrap" style={{
          padding: 3, borderRadius: "50%",
          background: isFirst
            ? T.gradient.goldPremium
            : `linear-gradient(135deg, ${color.fg} 0%, ${color.border} 100%)`,
          boxShadow: isFirst ? `0 0 20px ${T.gold}55` : "none",
        }}>
          <Avatar initials={initials} size={avatarSize} tone={color.fg} photo={c.foto} />
        </div>
        <div style={{
          position: "absolute", bottom: -4, right: -4,
          fontSize: 18, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))",
        }}>{medals[posicao]}</div>
      </div>

      {/* Pílula combinada (award + time) ACIMA do nome */}
      {awardId && (
        <div className="podio-pilula" style={{ marginTop: 6 }}>
          <PilulaAwardTime awardId={awardId} timeEmoji={timeEmoji} size={isFirst ? "sm" : "xs"} />
        </div>
      )}

      {/* Nome */}
      <div className="podio-nome serif" style={{
        fontSize: isFirst ? 13 : 12, color: souEu ? T.gold : T.brand,
        fontWeight: 600, textAlign: "center", letterSpacing: -0.2,
        lineHeight: 1.15, marginTop: 2,
      }}>
        {primeiroNome}
        {souEu && <div style={{ fontSize: 9, color: T.gold, fontWeight: 700, letterSpacing: 1, marginTop: 2 }}>VOCÊ</div>}
      </div>

      {/* Localização */}
      <div className="podio-loc" style={{
        fontSize: 9, color: T.textSub, textAlign: "center",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
        marginTop: -2,
      }}>
        📍 {c.cidade}-{c.uf}
      </div>

      {/* Pontos */}
      <div className="podio-pts serif" style={{
        fontSize: isFirst ? 22 : 17,
        fontWeight: 700, color: isFirst ? T.gold : color.fg,
        letterSpacing: -0.5, lineHeight: 1, marginTop: 4,
      }}>{c.pontosSemana || 0}</div>
      <div style={{ fontSize: 8, color: T.textDim, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginTop: -2 }}>pts semana</div>
    </div>
  );
}

// ─── PERFIL COMPACTO ─────────────────────────────────────────────────
function PerfilCompacto({ corretor: c, pts, award, prox, time, bonusTotal }) {
  const color = AWARD_COLORS[award.cor];
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const isDiamond = award.id === "diamond";

  return (
    <div style={{
      background: `linear-gradient(135deg, ${color.bg} 0%, ${T.card} 70%, ${T.cardSoft} 100%)`,
      border: `1px solid ${color.border}`,
      borderRadius: T.r.xl, padding: 22,
      position: "relative", overflow: "hidden",
      boxShadow: T.shadow.md,
    }}>
      {/* Mobile: estrutura foto → pílula → nome → localidade (escondido no desktop) */}
      <div className="profile-mobile-stack" style={{
        display: "none",
        flexDirection: "column", alignItems: "center", gap: 10,
        marginBottom: 16,
      }}>
        <Avatar initials={initials} size={64} photo={c.foto} />
        <PilulaAward awardId={award.id} />
        <div className="serif" style={{
          fontSize: 22, fontWeight: 600, color: T.brand,
          letterSpacing: -0.3, lineHeight: 1.1, textAlign: "center",
        }}>{c.nome}</div>
        <div style={{ fontSize: 11, color: T.textSub, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          {time && <span>{time.emoji || "🎯"} {time.nome}</span>}
          <span>📍 {c.cidade}-{c.uf}</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 16, alignItems: "center", position: "relative" }} className="hero-profile">
        {/* Avatar (visível só no desktop) */}
        <div className="profile-avatar-desktop">
          <Avatar initials={initials} size={56} photo={c.foto} />
        </div>

        {/* Identidade (visível só no desktop) */}
        <div className="profile-identity profile-identity-desktop" style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.brand, letterSpacing: -0.3, lineHeight: 1.1 }}>
              {c.nome}
            </div>
            <PilulaAward awardId={award.id} />
          </div>
          <div style={{ fontSize: 11, color: T.textSub, marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {time && <span>{time.emoji || "🎯"} {time.nome}</span>}
            <span>📍 {c.cidade}-{c.uf}</span>
          </div>
        </div>

        {/* Badge troféu voando + Pontuação total */}
        <div className="profile-badge-pontos" style={{
          display: "flex", alignItems: "center", gap: 12,
        }}>
          {award.id !== "cafe" ? (
            <img
              src={`./badges/${award.id}.png`}
              alt={award.nome}
              className="profile-badge-img"
              style={{
                height: 110,
                width: "auto",
                objectFit: "contain",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
                flexShrink: 0,
              }}
            />
          ) : (
            <div style={{
              fontSize: 64, lineHeight: 1, flexShrink: 0,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
            }}>☕</div>
          )}

          <div className="profile-pontos" style={{
            textAlign: "center", minWidth: 110,
            padding: "12px 16px", borderRadius: T.r.md,
            background: `linear-gradient(160deg, ${color.bg} 0%, ${T.card} 100%)`,
            border: `1px solid ${color.border}55`,
          }}>
            <div style={{ fontSize: 9, color: T.textSub, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>
              {isDiamond ? "Ranking Score" : "Pontos totais"}
            </div>
            <div className="serif" style={{
              fontSize: 42, fontWeight: 700, color: color.fg,
              letterSpacing: -2, lineHeight: 1, marginTop: 2,
            }}>
              {pts.total}
            </div>
            {bonusTotal > 0 && (
              <div style={{ fontSize: 10, color: T.ok, fontWeight: 700, marginTop: 4 }}>
                +{bonusTotal} pts aceleradores
              </div>
            )}
          </div>
        </div>
      </div>

      {prox && (
        <div style={{ marginTop: 16, padding: "10px 12px", background: T.bg + "cc", borderRadius: T.r.md, border: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textSub, marginBottom: 6 }}>
            <span>Próximo: <b style={{ color: AWARD_COLORS[prox.prox.cor].fg }}>{prox.prox.nome}</b></span>
            <span>Faltam <b style={{ color: T.brand }}>{prox.faltam}</b></span>
          </div>
          <Progress value={pts.total} max={prox.prox.pts} color={color.fg} height={6} />
        </div>
      )}

      {/* Link de indicação completo + copiar */}
      {(c.tagUnica || c.tag_unica) && (
        <div style={{ marginTop: 16, padding: "10px 12px", background: T.bg + "cc", borderRadius: T.r.md, border: `1px solid ${T.brand}33` }}>
          <div style={{ fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>🔗 Link de indicação</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a
              href={`https://app.bellanor.com.br/altaperformance?ref=${c.tagUnica || c.tag_unica}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1, fontSize: 11, color: "#87CEEB", background: T.bg,
                padding: "6px 10px", borderRadius: T.r.md, border: `1px solid #87CEEB44`,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                textDecoration: "underline", fontFamily: "monospace",
              }}
              title="Abrir link em nova aba"
            >{`https://app.bellanor.com.br/altaperformance?ref=${c.tagUnica || c.tag_unica}`}</a>
            <button onClick={() => {
              const link = `https://app.bellanor.com.br/altaperformance?ref=${c.tagUnica || c.tag_unica}`;
              navigator.clipboard.writeText(link).then(() => alert("Link copiado!")).catch(() => prompt("Copie:", link));
            }} style={{
              padding: "6px 12px", borderRadius: T.r.md,
              background: T.brand + "22", border: `1px solid ${T.brand}44`,
              color: T.brand, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit",
            }}>📋 Copiar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Pílula com APENAS o nome do award (sem badge image)
function PilulaAward({ awardId }) {
  const map = {
    cafe: "☕", bronze: "Bronze", silver: "Silver",
    gold: "Golden", platinum: "Platinum", diamond: "Diamond",
  };
  const c = AWARD_COLORS[awardId] || AWARD_COLORS.cafe;
  return (
    <span style={{
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      borderRadius: 100, padding: "3px 11px", fontSize: 11,
      fontWeight: 600, letterSpacing: 0.3,
      fontFamily: "'Fraunces', Georgia, serif", whiteSpace: "nowrap",
      display: "inline-block",
    }}>
      {map[awardId] || awardId}
    </span>
  );
}

// ─── SUA SEMANA — metas individuais + aceleradores conquistados ──────
function SuaSemana({ metasIndiv, bonusDetalhes, bonusPontos }) {
  const { volume, ativacoes, award, meta } = metasIndiv;
  const temBonus = bonusDetalhes && bonusDetalhes.length > 0;
  const ambasMetas = volume.atingiu && ativacoes.atingiu;

  return (
    <div style={{
      background: ambasMetas
        ? `linear-gradient(135deg, ${T.gold}15 0%, ${T.brandSoft} 100%)`
        : T.card,
      border: `1px solid ${ambasMetas ? T.gold + "55" : T.borderMid}`,
      borderRadius: T.r.lg,
      padding: "16px 18px",
      boxShadow: T.shadow.md,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: ambasMetas ? T.gold : T.textSub, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
          ✨ Sua semana
        </div>
        <div style={{ fontSize: 10, color: T.textDim }}>
          <b style={{ color: AWARD_COLORS[award.cor].fg }}>{award.nome}</b>
        </div>
      </div>

      {/* Metas individuais — 2 barras */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="grid-2-to-1">
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 5 }}>
            <span style={{ color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>💰 Volume</span>
            <span style={{ color: volume.atingiu ? T.ok : T.textSub, fontWeight: 700 }}>
              {volume.atingiu ? "✓ atingida" : `${volume.pct.toFixed(0)}%`}
            </span>
          </div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 5 }}>
            {fmt.brl(volume.atual)}
          </div>
          <Progress value={volume.atual} max={volume.meta} color={volume.atingiu ? T.ok : T.brand} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            meta da semana: {fmt.brl(volume.meta)}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 5 }}>
            <span style={{ color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>👥 Ativações</span>
            <span style={{ color: ativacoes.atingiu ? T.ok : T.textSub, fontWeight: 700 }}>
              {ativacoes.atingiu ? "✓ atingida" : `${ativacoes.pct.toFixed(0)}%`}
            </span>
          </div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 5 }}>
            {ativacoes.atual}
          </div>
          <Progress value={ativacoes.atual} max={Math.max(meta.ativacoes, 1)} color={ativacoes.atingiu ? T.ok : T.info} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            meta da semana: {meta.ativacoes === 0 ? "sem exigência" : `${meta.ativacoes} ativaç${meta.ativacoes === 1 ? "ão" : "ões"}`}
          </div>
        </div>
      </div>

      {/* Aceleradores conquistados (se houver) */}
      {temBonus && (
        <div style={{
          marginTop: 16, paddingTop: 14,
          borderTop: `1px solid ${T.border}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: T.gold, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>
              🏆 Acelerador conquistado
            </div>
            {bonusPontos > 0 && (
              <div style={{ fontSize: 11, color: T.ok, fontWeight: 700 }}>
                +{bonusPontos} pts bônus
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {bonusDetalhes.map((d, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                fontSize: 11, color: d.tipo === "descanso" ? T.textDim : T.text,
              }}>
                <span style={{ fontStyle: d.tipo === "descanso" ? "italic" : "normal" }}>
                  {d.tipo === "descanso" ? "⏸ " : "🏆 "}
                  {d.label}
                  {d.destaque && <span style={{ color: T.gold, marginLeft: 6 }}>· {d.destaque}</span>}
                </span>
                <b style={{ color: d.pct > 0 ? T.ok : T.textDim }}>
                  {d.pct > 0 ? `+${d.pct}%` : "—"}
                </b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PRÊMIOS ANUAIS (barra contínua com marcos) ───────────────────────
function PremiosAnuais({ elegibilidade, premios }) {
  // Volume atual (pega do primeiro item — todos têm o mesmo volumeAtual)
  const vendasAno = elegibilidade[0]?.volumeAtual || 0;
  const MAX = 5000000;
  const marcos = [
    { id: "iphone",   emoji: "📱", nome: "iPhone do ano",        vol: 2000000, cor: "#d7be7c", award: "Golden" },
    { id: "cruzeiro", emoji: "🚢", nome: "Cruzeiro de luxo (2p)", vol: 3000000, cor: "#b9c5d6", award: "Platinum" },
    { id: "byd",      emoji: "🚗", nome: "BYD 100% elétrico",     vol: 5000000, cor: "#9bd2e6", award: "Diamond" },
  ];
  const pctTotal = Math.min(100, (vendasAno / MAX) * 100);

  return (
    <Card>
      <style>{`
        @keyframes premioConquistado {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.25) rotate(-5deg); }
          100% { transform: scale(1.08); }
        }
        @keyframes premioPulse {
          0%, 100% { box-shadow: 0 0 0 0 transparent; }
          50%      { box-shadow: 0 0 0 8px rgba(215,190,124,0.25); }
        }
        .premio-conquistado-d { animation: premioConquistado 1.2s ease-out forwards, premioPulse 2.4s ease-in-out infinite 1.2s; }
        @keyframes barraShineD {
          0%   { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
        .barra-shine-d {
          background: linear-gradient(90deg, #d7be7c 0%, #f5e1a8 50%, #d7be7c 100%);
          background-size: 200% 100%;
          animation: barraShineD 3s linear infinite;
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
          🎁 Prêmios Anuais {new Date().getFullYear()}
        </div>
        <div style={{ fontSize: 10, color: T.textDim }}>Zera em 1/jan</div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.gold }}>{fmt.brl(vendasAno)}</div>
        <div style={{ fontSize: 10, color: T.textSub }}>meta máx {fmt.brl(MAX)}</div>
      </div>

      {/* Barra contínua com marcos */}
      <div style={{ position: "relative", height: 64, marginBottom: 18 }}>
        <div style={{
          position: "absolute", left: 0, right: 0, top: 28,
          height: 8, background: T.elev, borderRadius: 4,
        }} />
        <div className={pctTotal > 0 ? "barra-shine-d" : ""} style={{
          position: "absolute", left: 0, top: 28,
          width: `${pctTotal}%`, height: 8, borderRadius: 4,
          transition: "width 800ms cubic-bezier(.2,.7,.3,1.2)",
        }} />
        {marcos.map(m => {
          const pos = (m.vol / MAX) * 100;
          const ok = vendasAno >= m.vol;
          return (
            <div key={m.id} style={{
              position: "absolute", left: `${pos}%`, top: 0,
              transform: "translateX(-50%)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              color: m.cor,
            }}>
              <div className={ok ? "premio-conquistado-d" : ""} style={{
                fontSize: 26, lineHeight: 1,
                filter: ok ? `drop-shadow(0 0 8px ${m.cor})` : "grayscale(60%) opacity(0.5)",
                transition: "filter 400ms",
              }}>{m.emoji}</div>
              <div style={{ width: 2, height: 12, background: ok ? m.cor : T.borderMid, marginTop: 2 }} />
              <div style={{
                fontSize: 9, color: ok ? m.cor : T.textDim, fontWeight: 700,
                whiteSpace: "nowrap", marginTop: 2,
              }}>{(m.vol / 1000000).toFixed(0)}M</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 6, marginTop: 36 }}>
        {marcos.map(m => {
          const ok = vendasAno >= m.vol;
          return (
            <div key={m.id} style={{
              padding: "6px 8px", borderRadius: T.r.md,
              background: ok ? m.cor + "22" : "transparent",
              border: `1px solid ${ok ? m.cor + "55" : T.border}`,
              fontSize: 9, textAlign: "center",
            }}>
              <div style={{ color: m.cor, fontWeight: 700 }}>{m.emoji} {m.nome}</div>
              <div style={{ color: ok ? T.ok : T.textDim, marginTop: 2 }}>
                {ok ? "🏆 Conquistado" : `R$ ${(m.vol / 1000000).toFixed(0)}M`}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── CARD DE META (time/regional/nacional — formato unificado) ───────
function MetaCard({ titulo, nome, valor, ativados, metaVol, metaAtiv, onVerMais, subtitle }) {
  const bateuVol = valor >= metaVol;
  const bateuAtiv = ativados >= metaAtiv;
  const st = bateuVol && bateuAtiv ? "supermeta"
           : bateuVol ? "volume"
           : bateuAtiv ? "ativacoes"
           : "parcial";
  const info = {
    supermeta:  { label: "Supermeta 🏆", color: T.ok,      icon: "🏆" },
    volume:     { label: "Meta volume",  color: T.brand,   icon: "💰" },
    ativacoes:  { label: "Meta ativações", color: T.info,  icon: "👥" },
    parcial:    { label: "Em progresso", color: T.textSub, icon: "○" },
  }[st];

  const pctVol  = Math.min((valor / metaVol)    * 100, 100);
  const pctAtiv = Math.min((ativados / metaAtiv) * 100, 100);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div className="serif" style={{ fontSize: 16, fontWeight: 600, color: T.brand, letterSpacing: -0.2 }}>
            {titulo}
          </div>
          <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
            {nome}{subtitle && <span style={{ color: T.textDim }}> · {subtitle}</span>}
          </div>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 700, color: info.color,
          padding: "3px 10px", borderRadius: 100,
          background: info.color + "18", border: `1px solid ${info.color}55`,
          letterSpacing: 0.5, textTransform: "uppercase",
          display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          {info.icon} {info.label}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="grid-2-to-1">
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4 }}>
            <span style={{ color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>💰 Volume</span>
            <span style={{ color: bateuVol ? T.ok : T.textSub, fontWeight: 700 }}>
              {bateuVol ? "✓" : `${pctVol.toFixed(0)}%`}
            </span>
          </div>
          <div className="serif" style={{ fontSize: 16, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 4 }}>
            {fmt.brl(valor)}
          </div>
          <Progress value={valor} max={metaVol} color={T.brand} height={4} />
          <div style={{ fontSize: 9, color: T.textDim, marginTop: 3 }}>meta {fmt.brl(metaVol)}</div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4 }}>
            <span style={{ color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>👥 Ativações</span>
            <span style={{ color: bateuAtiv ? T.ok : T.textSub, fontWeight: 700 }}>
              {bateuAtiv ? "✓" : `${pctAtiv.toFixed(0)}%`}
            </span>
          </div>
          <div className="serif" style={{ fontSize: 16, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 4 }}>
            {ativados}
          </div>
          <Progress value={ativados} max={metaAtiv} color={T.info} height={4} />
          <div style={{ fontSize: 9, color: T.textDim, marginTop: 3 }}>meta {metaAtiv} ativações</div>
        </div>
      </div>

      {onVerMais && (
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <button onClick={onVerMais} style={{
            background: "none", border: "none", cursor: "pointer",
            color: T.textSub, fontSize: 11, fontWeight: 500,
            display: "inline-flex", alignItems: "center", gap: 4,
            fontFamily: "inherit",
          }}
            onMouseEnter={e => e.currentTarget.style.color = T.brand}
            onMouseLeave={e => e.currentTarget.style.color = T.textSub}
          >
            Ver detalhes <Icon.arrow size={11} />
          </button>
        </div>
      )}
    </Card>
  );
}

// ─── TOP 3 DO MÊS ────────────────────────────────────────────────────
function TopMesCard({ top3, meuId, minhaPos, times }) {
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
          🏅 Top 3 Nacional do mês
        </div>
        <div style={{ fontSize: 10, color: T.textDim, maxWidth: 380, textAlign: "right" }}>
          Ganha aceleração de pontos e menção de destaque.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {top3.map((c, i) => {
          const souEu = c.id === meuId;
          const medals = ["🥇", "🥈", "🥉"];
          const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
          const aw = calcAwardVitalicio(c);
          const timeDoc = c.timeId && times ? times.find(t => t.id === c.timeId) : null;
          return (
            <div key={c.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 12px",
              background: souEu ? T.brandSoft : "transparent",
              border: souEu ? `1px solid ${T.brand}55` : "1px solid transparent",
              borderRadius: T.r.md,
            }}>
              <span style={{ fontSize: 20 }}>{medals[i]}</span>
              <Avatar initials={initials} size={32} photo={c.foto} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Linha 1: Nome + Pílula (colados) */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: souEu ? T.gold : T.text, fontWeight: souEu ? 700 : 500 }}>
                    {c.nome}
                  </span>
                  {souEu && <span style={{ fontSize: 9, color: T.gold, fontWeight: 700, letterSpacing: 1 }}>· VOCÊ</span>}
                  <PilulaAwardTime awardId={aw.id} timeEmoji={timeDoc?.emoji} size="xs" />
                </div>
                {/* Linha 2: Cidade + Pontos (colados) */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 10, color: T.textSub }}>📍 {c.cidade}-{c.uf}</span>
                  <span style={{ fontSize: 10, color: T.textSub, fontWeight: 600 }}>{c.pontosMes} pts</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {minhaPos === -1 && (
        <div style={{
          marginTop: 10, padding: "8px 12px",
          background: T.cardSoft, borderRadius: T.r.md,
          fontSize: 11, color: T.textSub, fontStyle: "italic", textAlign: "center",
        }}>
          Pontue mais no mês para entrar no Top 3 e ganhar acelerador de pontos + menção de destaque no nosso grupo do WhatsApp.
        </div>
      )}
    </Card>
  );
}

// ─── ATALHOS RÁPIDOS ─────────────────────────────────────────────────
function AtalhosRapidos({ onNavigate, userModules }) {
  const opts = [
    { id: "simulador", icon: "📐", label: "Simulador" },
    { id: "ia",        icon: "🤖", label: "IA" },
  ].filter(o => userModules.includes(o.id));

  if (opts.length === 0) return null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${opts.length}, 1fr)`, gap: 8 }} className="atalhos-grid">
      {opts.map(o => (
        <button
          key={o.id}
          onClick={() => onNavigate?.(o.id)}
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: T.r.md,
            padding: "14px 10px",
            cursor: "pointer",
            color: T.text,
            fontFamily: "inherit",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            transition: `all 160ms ${T.ease}`,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = T.cardHi;
            e.currentTarget.style.borderColor = T.brand + "66";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = T.card;
            e.currentTarget.style.borderColor = T.border;
          }}
        >
          <span style={{ fontSize: 22 }}>{o.icon}</span>
          <span style={{ fontSize: 11, fontWeight: 500 }}>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// BÔNUS DE TARGETS (individual do corretor logado)
// ═══════════════════════════════════════════════════════════════════════
function BonusTargetsCard({ corretorUuid }) {
  const [comissoes, setComissoes] = useState([]);

  useEffect(() => {
    if (!corretorUuid || !isSupabaseReady) return;
    (async () => {
      const { data } = await supabase.from("comissoes").select("*").eq("corretor_id", corretorUuid);
      setComissoes(data || []);
    })();
  }, [corretorUuid]);

  const agora = new Date();
  const umAno = new Date(agora); umAno.setFullYear(agora.getFullYear() - 1);
  const noventa = new Date(agora); noventa.setDate(agora.getDate() - 90);
  const inicioAno = new Date(agora.getFullYear(), 0, 1);

  const redeAno = comissoes
    .filter(c => (c.nivel ?? 0) > 0 && new Date(c.criado_em) >= umAno)
    .reduce((s, c) => s + parseFloat(c.comissao_valor ?? c.valor ?? 0), 0);
  const volume90 = comissoes
    .filter(c => (c.nivel ?? 0) === 0 && new Date(c.criado_em) >= noventa)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);
  const vendasAno = comissoes
    .filter(c => (c.nivel ?? 0) === 0 && new Date(c.criado_em) >= inicioAno)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);

  const targets = [
    { id: "rede",     titulo: "Desempenho de rede",     alvo: 10000,   atual: redeAno,   bonus: 1000, label: "R$ 10k em comissão de rede (último ano)" },
    { id: "melhoria", titulo: "Melhoria de desempenho", alvo: 1000000, atual: volume90,  bonus: 2000, label: "R$ 1M em volume (últimos 90 dias)" },
    { id: "anual",    titulo: "Desempenho anual",       alvo: 4000000, atual: vendasAno, bonus: 3000, label: "R$ 4M em vendas no ano" },
  ];

  return (
    <Card>
      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
        🎯 Bônus de Targets
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {targets.map(t => {
          const pct = Math.min(100, (t.atual / t.alvo) * 100);
          const ok = t.atual >= t.alvo;
          return (
            <div key={t.id} style={{
              padding: "10px 12px", borderRadius: T.r.md,
              background: ok ? T.ok + "15" : T.cardSoft,
              border: `1px solid ${ok ? T.ok + "44" : T.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{ok && "✅ "}{t.titulo}</div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{t.label}</div>
                </div>
                <div className="serif" style={{ fontSize: 18, fontWeight: 700, color: ok ? T.ok : T.gold }}>+{fmt.brl(t.bonus)}</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: T.textSub, display: "flex", justifyContent: "space-between" }}>
                <span>{fmt.brl(t.atual)} de {fmt.brl(t.alvo)}</span>
                <span style={{ color: ok ? T.ok : T.brand, fontWeight: 600 }}>{pct.toFixed(0)}%</span>
              </div>
              <div style={{ marginTop: 4, height: 4, background: T.elev, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: ok ? T.ok : T.brand, transition: "width 320ms" }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
